import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { Box, MenuItem, Stack, Typography } from "@mui/material";
import { FC, ReactElement, ReactNode, useContext, useRef, useState } from "react";
import { FaIcon } from "../../../DataDisplay/Icon/FaIcon";
import { SubmenuHeightContext, SUBMENU_TRANSITION_DURATION } from "./SrSelectContext";

/**
 * Select Sub-menu
 */
export type SrSelectSubmenuProps = {
  icon?: ReactElement;
  label: string;
  subLabel?: string;
  backLabel?: string;
  largeItems?: boolean;
  children: ReactNode;
};

export const SrSelectSubmenu: FC<SrSelectSubmenuProps> = ({
  icon,
  label,
  subLabel,
  backLabel,
  largeItems,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLUListElement | null>(null);
  const ctx = useContext(SubmenuHeightContext);

  const openHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
    // Measure next frame so layout settles
    requestAnimationFrame(() => {
      const h = panelRef.current?.scrollHeight || 0;
      ctx?.openSubmenu(h);
    });
  };

  const backHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    ctx?.closeSubmenu();
  };

  const largeItemStyle = largeItems
    ? {
        display: "flex",
        alignItems: "center",
        minHeight: "2.6em",
      }
    : {};

  return (
    <MenuItem
      onClick={openHandler}
      disableRipple
      sx={{
        position: open ? "absolute" : "relative",
        top: 0,
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
        // Icon, if added
        "& > span > svg": {
          float: "left",
          fontSize: "20px",
          mr: "14px",
          mt: "10px",
        },
      }}
    >
      <Box component="span" sx={{ display: "block" }}>
        {icon || null}
        <Typography component="span" noWrap sx={{ display: "block" }}>
          {label}
        </Typography>
        {subLabel && (
          <Typography
            variant="body2"
            component="span"
            noWrap
            sx={{ display: "block", color: "on.surfaceDimmer" }}
          >
            {subLabel}
          </Typography>
        )}
      </Box>
      <FaIcon className="trigger" icon={faChevronRight} />
      <Stack
        ref={panelRef}
        component="ul"
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          backgroundColor: "Menu.bg",
          transform: `translateX(${open ? "0%" : "100%"})`,
          transition: `transform ${SUBMENU_TRANSITION_DURATION} ease`,
          zIndex: 1,
          "& li > div": largeItemStyle,
        }}
      >
        <MenuItem
          onClick={backHandler}
          disableRipple
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <FaIcon icon={faChevronLeft} />
          <Typography
            component="div"
            noWrap
            sx={{
              ...largeItemStyle,
              color: "on.surfaceDimmer",
            }}
          >
            {backLabel || "Back"}
          </Typography>
        </MenuItem>
        {children}
      </Stack>
    </MenuItem>
  );
};

SrSelectSubmenu.displayName = "SrSelectSubmenu";
